import { useEffect, useRef } from 'react';

export default function Hero() {
  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const bgRef = useRef<HTMLImageElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleParallax = () => {
      const scrollY = window.scrollY;
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${scrollY * 0.4}px) scale(1.1)`;
      }
      if (gridRef.current) {
        gridRef.current.style.transform = `translateY(${scrollY * 0.15}px)`;
      }
      if (contentRef.current) {
        contentRef.current.style.transform = `translateY(${scrollY * 0.12}px)`;
        contentRef.current.style.opacity = `${1 - scrollY * 0.0015}`;
      }
    };

    window.addEventListener('scroll', handleParallax, { passive: true });
    return () => window.removeEventListener('scroll', handleParallax);
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-center overflow-hidden bg-white"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          ref={bgRef}
          src="https://ilkeismakinalari.com/img/home-background.jpg"
          alt="İş Makinaları Yedek Parça"
          className="w-full h-full object-cover object-center will-change-transform"
          style={{ transformOrigin: 'center top' }}
        />
        {/* Strong left fade for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20"></div>
        {/* Bottom fade into page */}
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
        {/* Top subtle fade */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-transparent"></div>
        {/* Orange accent tint on right edge */}
        <div className="absolute inset-0 bg-gradient-to-l from-orange-400/15 via-transparent to-transparent"></div>
        {/* Subtle grid overlay */}
        <div
          ref={gridRef}
          className="absolute inset-0 will-change-transform"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,120,0,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(255,120,0,0.25) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            opacity: 0.035,
          }}
        ></div>
      </div>

      {/* Decorative vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-orange-500 to-transparent opacity-60"></div>

      {/* Content */}
      <div ref={contentRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-32 will-change-transform">
        <div className="max-w-3xl">
          {/* Tag line */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-px bg-orange-500"></div>
            <span className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase">
              25 Yıllık Sektör Deneyimi
            </span>
          </div>

          {/* Main heading */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 leading-none mb-4 tracking-tight">
            İŞ
            <br />
            <span className="text-transparent" style={{ WebkitTextStroke: '2px rgba(0,0,0,0.20)' }}>
              MAKİNALARI
            </span>
            <br />
            <span className="text-orange-500">YEDEK PARÇA</span>
          </h1>

          <div className="w-24 h-1 bg-orange-500 mb-8"></div>

          <p className="text-gray-600 text-lg md:text-xl leading-relaxed mb-12 max-w-xl">
            Komatsu, Caterpillar, Volvo, Hidromek ve daha fazlası için
            <strong className="text-gray-900 font-semibold"> kesintisiz tedarik</strong>.
            Çeyrek asırlık tecrübemizle sahadaki yükünüzü hafifletiyoruz.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button
              onClick={() => handleScroll('#iletisim')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-sm transition-all duration-200 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer uppercase tracking-widest text-sm"
            >
              <span>Teklif Al</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </button>
            <button
              onClick={() => handleScroll('#markalar')}
              className="border border-gray-300 hover:border-orange-500/60 text-gray-600 hover:text-orange-500 font-semibold px-10 py-4 rounded-sm transition-all duration-200 whitespace-nowrap cursor-pointer uppercase tracking-widest text-sm bg-white/70 backdrop-blur-sm"
            >
              Markalarımız
            </button>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-8">
            {[
              { value: '25+', label: 'Yıl Deneyim' },
              { value: '50.000+', label: 'Parça Çeşidi' },
              { value: '18+', label: 'Marka' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="text-2xl font-black text-orange-500">{stat.value}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wider mt-0.5">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-8 flex items-center gap-3 text-gray-400">
        <div className="w-px h-12 bg-gradient-to-b from-transparent to-orange-500/50"></div>
        <span className="text-xs uppercase tracking-widest">Aşağı Kaydır</span>
      </div>
    </section>
  );
}
